import React, { useEffect, useRef, useState } from 'react';

import { useCurrentTheme } from 'app/shared/theme';
import { useAnalyticsContext } from 'app/shared/utils';
import { truncateByChar } from 'app/shared/utils/string';
import useDisableScroll from 'app/shared/utils/useDisableScroll';
import useKeyDown from 'app/shared/utils/useKeyDown';
import { WithInputStyleProps } from 'app/shared/components/styleUtils';
import {
  Box,
  Icon,
  InputHelperText,
  InputLabel,
  InputStatusIcon,
  Spinner,
  TextInput,
} from 'app/shared/components/atoms';
import { ReactComponent as ChevronDown } from 'icons/chevron-down.svg';
import { ReactComponent as ChevronUp } from 'icons/chevron-up.svg';
import { ReactComponent as Search } from 'icons/search.svg';

import { Options, SharedSelectProps } from './SelectOptions';

interface Props<T>
  extends React.ComponentPropsWithoutRef<'select'>,
    WithInputStyleProps,
    SharedSelectProps<T> {
  label: string | null;
  initialWidth?: string;
  isDisabled?: boolean;
  hasDataError?: boolean;
  hasValidationError?: boolean;
  handleOptionClick?: (option: any) => void;
  onClickResetInput?: () => void;
  onOpen?: () => void;
  id?: string;
  getOptionLabel: (o: T) => string;
  isSearchable?: boolean;
  onSearch?: (input: string) => void;
  filterBy?: (option: T, query?: string) => boolean;
  value?: string | number;
  defaultValue?: any | null;
  helperText?: string | React.ReactNode | null;
  labelColor?: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  iconColor?: string;
  isRequired?: boolean;
  isMultiSelect?: boolean;
  onResetMultiSelect?: () => void;
  onClose?: () => void;
  moreOptionsTitle?: string;
  multiSelectSelectedTitle?: string;
  defaultMultiSelectValue?: any | null;
  multiSelectValue?: (string | number)[];
  disableScrollWhenOpen?: boolean;
}

export const Select = <T = any,>({
  label,
  helperText,
  options,
  noOptionsMessage,
  isLoading = false,
  isDisabled = false,
  hasDataError = false,
  hasValidationError = false,
  isSuccessful = false,
  handleOptionClick,
  onClickResetInput,
  onResetMultiSelect,
  name,
  id,
  getOptionLabel,
  getOptionValue,
  isSearchable,
  onSearch,
  filterBy,
  value,
  backgroundColor,
  labelColor,
  color,
  invertColors,
  groupBy,
  onOpen,
  defaultValue,
  initialWidth,
  mostVisitedOptions,
  recommendedOptions,
  noOptionsOption,
  iconColor,
  placeholderColor,
  placeholder,
  isRequired,
  getRecommendedOptionsTitle,
  getMostVisitedOptionsTitle,
  isMultiSelect = false,
  multiSelectValue,
  onClose,
  moreOptionsTitle,
  multiSelectSelectedTitle,
  icon = Search,
  zIndex = 1007,
  disableScrollWhenOpen = false,
  'data-qaid': qaId,
}: Props<T>) => {
  const { ruler, colors, borderRadius, fontSizes } = useCurrentTheme();
  const { trackAnalyticsEvent } = useAnalyticsContext();

  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [singleSelectValue, setSingleSelectValue] = useState<string | number>(
    isMultiSelect ? '' : defaultValue || value || ''
  );
  const [truncatedMultiSelectDisplay, setTruncatedMultiSelectDisplay] =
    useState('');

  useDisableScroll(isOpen, disableScrollWhenOpen);

  const initialMultiSelectValues = multiSelectValue
    ? multiSelectValue
    : Array.isArray(defaultValue)
      ? defaultValue
      : Array.isArray(value)
        ? value
        : [];

  const [multiSelectValues, setMultiSelectValues] = useState<
    (string | number)[]
  >(initialMultiSelectValues);

  const [searchQuery, setSearchQuery] = useState('');

  const [filteredOptions, setFilteredOptions] = useState<any>(options);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      const target = event.target as Node;

      const clickedOutside =
        selectRef.current &&
        !selectRef.current.contains(target) &&
        optionsRef.current &&
        !optionsRef.current.contains(target);

      if (clickedOutside) {
        if (isOpen && onClose) {
          onClose();
        }

        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, onClose]);

  useKeyDown('Escape', () => closeDropdown());

  const onOptionClick = (option: any) => {
    if (isMultiSelect) {
      resetSearch();
      setMultiSelectValues((prev) => {
        const optionValue = getOptionValue(option);
        const alreadySelected = prev.includes(optionValue);
        const updatedSelection = alreadySelected
          ? prev.filter((v) => v !== optionValue)
          : [...prev, optionValue];
        handleOptionClick && handleOptionClick(updatedSelection);
        return updatedSelection;
      });
    } else {
      const label = getOptionLabel(option);
      setSingleSelectValue(label);
      setIsOpen(false);
      handleOptionClick && handleOptionClick(option);
    }
  };

  useEffect(() => {
    if (isMultiSelect) {
      if (Array.isArray(value)) {
        setMultiSelectValues(value);
      }
    } else {
      if (value !== undefined) {
        setSingleSelectValue(value);
      }
    }
  }, [value, isMultiSelect]);

  useEffect(() => {
    if (isMultiSelect) {
      const labels = (multiSelectValue || [])
        .map((val) => {
          const opt = options.find((o: any) => getOptionValue(o) === val);
          return opt ? getOptionLabel(opt) : null;
        })
        .filter(Boolean);

      const fullText = labels.join(', ');
      const truncated =
        fullText.length > 30 ? truncateByChar(fullText, 27) : fullText;

      setTruncatedMultiSelectDisplay(truncated);
      if (multiSelectValue) {
        setMultiSelectValues(multiSelectValue);
      }
    }
  }, [
    multiSelectValue,
    options,
    getOptionLabel,
    getOptionValue,
    isMultiSelect,
  ]);

  function resetSearch() {
    setSearchQuery('');
    setFilteredOptions(options);
    if (!isMultiSelect) {
      setSingleSelectValue('');
    }

    onSearch?.('');
    setIsOpen(true);
  }

  const handleSearch = (searchString: string) => {
    setSearchQuery(searchString);

    if (!options) {
      return;
    }

    if (searchString.trim() === '') {
      setFilteredOptions(options);
      onSearch?.('');
      return;
    }

    const optionsToFilterFrom = isMultiSelect
      ? options.filter(
          (opt: T) => !multiSelectValues.includes(getOptionValue(opt))
        )
      : options;

    const newFilteredOptions = optionsToFilterFrom.filter((o: T) => {
      if (filterBy) {
        return filterBy(o, searchString);
      }

      const label = getOptionLabel(o);
      return label?.toLowerCase().includes(searchString.toLowerCase());
    });

    trackAnalyticsEvent('Select Searched', {
      newFilteredOptions,
      searchString,
      id,
    });

    setFilteredOptions(newFilteredOptions);
    onSearch?.(searchString);
    setIsOpen(true);
  };

  const hasError = hasValidationError || hasDataError;
  const getInputStatusIconType = () => {
    if (hasError) {
      return 'ERROR';
    }
    if (!isDisabled && (!!value || !!searchQuery || !!singleSelectValue)) {
      return 'RESET';
    }

    return null;
  };
  const inputStatusIconType = getInputStatusIconType();

  function handleClick() {
    if (isOpen && !isMultiSelect) {
      !isSearchable && setIsOpen(false);
      return;
    }

    setIsOpen(true);
    onOpen && onOpen();
  }

  const displayValue = isMultiSelect
    ? truncatedMultiSelectDisplay
    : singleSelectValue;

  const closeDropdown = () => {
    if (isOpen) {
      onClose?.();
      setIsOpen(false);
    }
  };
  const shouldDisplaySearchIcon = isSearchable && !isDisabled;
  const isFocused = isOpen && !isSearchable;

  return (
    <Box
      position="relative"
      w="100%"
      h="fit-content"
      minWidth="fit-content"
      flexDirection="column"
      alignItems="stretch"
      backgroundColor={backgroundColor}
      data-qaid="select-container"
      borderRadius={borderRadius.input}
    >
      {label && (
        <InputLabel
          name={name}
          label={label}
          labelColor={labelColor}
          hasError={hasError}
          isRequired={isRequired}
        />
      )}
      <Box flexDirection="row" alignItems="center" onClick={handleClick}>
        <TextInput
          data-qaid={`${qaId}-input`}
          pl={shouldDisplaySearchIcon ? [10, 10, 11, 11] : undefined}
          ref={selectRef}
          disabled={isDisabled}
          hasError={hasError}
          backgroundColor={backgroundColor}
          placeholderColor={placeholderColor}
          color={color}
          invertColors={invertColors}
          name={name}
          id={id}
          onChange={(e: any) => handleSearch(e.target.value)}
          autoComplete="off"
          value={isSearchable && isOpen ? searchQuery : displayValue}
          width={initialWidth}
          aria-required={isRequired}
          placeholder={placeholder}
          readOnly={!isSearchable}
          borderRadiusBottomLeft={isFocused ? '0px' : undefined}
          borderRadiusBottomRight={isFocused ? '0px' : undefined}
          isFocused={isFocused}
          cursor={isSearchable ? undefined : 'pointer'}
        />
        {shouldDisplaySearchIcon && (
          <Box
            position="absolute"
            left={`${ruler[4]}px`}
            alignItems="center"
            onClick={() => undefined}
          >
            <Icon
              icon={icon}
              size="md"
              strokeColor={iconColor || colors.primary.black.base}
              fillColor="transparent"
            />
          </Box>
        )}
        <Options<T>
          data-qaid={qaId}
          groupBy={groupBy}
          isOpen={isOpen}
          options={filteredOptions}
          unfilteredOptions={options}
          noOptionsMessage={noOptionsMessage}
          recommendedOptions={recommendedOptions}
          mostVisitedOptions={mostVisitedOptions}
          isLoading={!!isLoading}
          hasError={!!hasDataError}
          onOptionClick={onOptionClick}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          onBlur={closeDropdown}
          isSearchable={isSearchable}
          userIsSearching={isSearchable && !!searchQuery}
          noOptionsOption={noOptionsOption}
          invertColors={invertColors}
          selectRef={selectRef}
          getRecommendedOptionsTitle={getRecommendedOptionsTitle}
          getMostVisitedOptionsTitle={getMostVisitedOptionsTitle}
          isMultiSelect={isMultiSelect}
          multiSelectValues={multiSelectValues}
          onResetMultiSelect={() => {
            resetSearch();
            setMultiSelectValues([]);
            onResetMultiSelect && onResetMultiSelect();
            closeDropdown();
          }}
          optionsRef={optionsRef}
          multiSelectSelectedTitle={multiSelectSelectedTitle}
          moreOptionsTitle={moreOptionsTitle}
          zIndex={zIndex}
        />
        {isLoading && (
          <Box position="absolute" right={`${ruler[4]}px`}>
            <Spinner
              size={fontSizes.fontSize300.mobile}
              invert
              data-qaid={`${qaId}-spinner`}
            />
          </Box>
        )}
        {!isLoading && isSearchable && (
          <InputStatusIcon
            iconType={inputStatusIconType}
            onClickResetInput={() => {
              resetSearch();
              onClickResetInput && onClickResetInput();
            }}
            invertColors={invertColors}
            backgroundColor={backgroundColor}
          />
        )}
        {!isSearchable && !isDisabled && (
          <Icon
            icon={isOpen ? ChevronUp : ChevronDown}
            size="sm"
            strokeColor={
              invertColors
                ? colors.primary.white.base
                : colors.primary.black.base
            }
            fillColor="transparent"
            position="absolute"
            right={`${ruler[4]}px`}
            pointerEvents="none"
          />
        )}
      </Box>
      {helperText && (
        <InputHelperText
          isDisabled={isDisabled}
          hasError={hasError}
          isSuccessful={isSuccessful}
          invertColors={invertColors}
          data-qaid={`${qaId}-helper-text`}
        >
          {helperText}
        </InputHelperText>
      )}
    </Box>
  );
};
